import { expandGroupSelection, normalizeGroups } from "./canvas-groups";
import { z } from "zod";
import type { Edge, Node } from "@xyflow/react";
import { boardSnapshotSchema } from "../validation/board-snapshot";
import { persistableSnapshot } from "./board-document";
import type { CanvasGraph } from "./canvas-commands";
import { getNodeCapabilities, isStickyColor } from "./node-formatting";

export type CanvasClipboard = CanvasGraph & { format: "trillion-board-clipboard"; version: 1 };
export type ClipboardResult = { ok: true; value: CanvasClipboard }
  | { ok: false; reason: "invalid" | "unsupported" | "empty" };

const finite = z.number().finite();
const point = z.object({ x: finite, y: finite }).passthrough();
const coordinate = z.tuple([finite, finite]);
const style = z.record(z.string(), z.union([z.string(), finite, z.null()]));
const position = z.enum(["top", "right", "bottom", "left"]);
const marker = z.union([z.string(), z.object({
  type: z.enum(["arrow", "arrowclosed"]), color: z.string().nullable().optional(),
  width: finite.nonnegative().optional(), height: finite.nonnegative().optional(),
  markerUnits: z.string().optional(), orient: z.string().optional(), strokeWidth: finite.nonnegative().optional(),
}).passthrough()]);
const presentation = {
  style: style.optional(), className: z.string().optional(), zIndex: finite.optional(),
  hidden: z.boolean().optional(), selectable: z.boolean().optional(), selected: z.boolean().optional(),
  deletable: z.boolean().optional(), focusable: z.boolean().optional(), ariaLabel: z.string().optional(), ariaRole: z.string().optional(),
  domAttributes: z.never().optional(),
};
const nodeSchema = boardSnapshotSchema.shape.nodes.element.extend({
  ...presentation,
  width: finite.nonnegative().optional(), height: finite.nonnegative().optional(),
  initialWidth: finite.nonnegative().optional(), initialHeight: finite.nonnegative().optional(),
  positionAbsolute: point.optional(),
  origin: z.tuple([finite.min(0).max(1), finite.min(0).max(1)]).optional(),
  sourcePosition: position.optional(), targetPosition: position.optional(),
  draggable: z.boolean().optional(), connectable: z.boolean().optional(),
  dragging: z.boolean().optional(), resizing: z.boolean().optional(), expandParent: z.boolean().optional(),
  measured: z.object({ width: finite.nonnegative().optional(), height: finite.nonnegative().optional() }).passthrough().optional(),
  dragHandle: z.string().optional(), parentId: z.string().optional(), parentNode: z.string().optional(),
  extent: z.union([z.literal("parent"), z.tuple([coordinate, coordinate]), z.null()]).optional(),
  handles: z.array(z.object({ id: z.string().nullable().optional(), type: z.enum(["source", "target"]),
    position, x: finite, y: finite, width: finite.nonnegative().optional(), height: finite.nonnegative().optional() }).passthrough()).optional(),
});
const edgeSchema = boardSnapshotSchema.shape.edges.unwrap().element.extend({
  ...presentation,
  sourceHandle: z.string().nullable().optional(), targetHandle: z.string().nullable().optional(),
  label: z.union([z.string(), finite]).optional(), labelStyle: style.optional(), labelBgStyle: style.optional(),
  labelShowBg: z.boolean().optional(), labelBgPadding: coordinate.optional(), labelBgBorderRadius: finite.nonnegative().optional(),
  markerStart: marker.optional(), markerEnd: marker.optional(), animated: z.boolean().optional(),
  reconnectable: z.union([z.boolean(), z.enum(["source", "target"])]).optional(),
  interactionWidth: finite.nonnegative().optional(),
  pathOptions: z.object({ offset: finite.optional(), borderRadius: finite.nonnegative().optional(),
    curvature: finite.optional() }).passthrough().optional(),
});
const envelopeSchema = z.object({ format: z.literal("trillion-board-clipboard"), version: z.literal(1),
  nodes: z.array(nodeSchema).min(1), edges: z.array(edgeSchema) }).strict();
const edgeTypes = new Set(["custom", "default", "straight", "step", "smoothstep", "simplebezier"]);
const textFields: Record<string, string[]> = {
  rectangle: ["label", "bgColor", "borderColor"], circle: ["label", "bgColor", "borderColor"],
  diamond: ["label", "bgColor", "borderColor"], hexagon: ["label", "bgColor", "borderColor"],
  stickyNote: ["text"], text: ["text"], orgChartCard: ["name", "title", "department", "avatarUrl", "bgColor"],
};
const customDataSchema = z.object({ label: z.string().optional(),
  lineStyle: z.enum(["solid", "dashed", "dotted"]).optional(),
  arrowMode: z.enum(["none", "end", "start", "both"]).optional(),
  pathStyle: z.enum(["smoothstep", "straight", "bezier"]).optional(),
}).passthrough();

function validate(value: unknown): ClipboardResult {
  // A source selection must obey the same JSON boundary as imported clipboard text.
  if (!z.json().safeParse(value).success) return { ok: false, reason: "invalid" };
  const parsed = envelopeSchema.safeParse(value);
  if (!parsed.success) return { ok: false, reason: "invalid" };
  const { nodes, edges } = parsed.data;
  const nodeIds = new Set<string>(), edgeIds = new Set<string>();
  for (const node of nodes) {
    const support = getNodeCapabilities(node.type);
    if (!support || node.parentId || node.parentNode || node.extent === "parent") return { ok: false, reason: "unsupported" };
    if (nodeIds.has(node.id)) return { ok: false, reason: "invalid" };
    nodeIds.add(node.id);
    const data = node.data ?? {};
    if ((data.type !== undefined && data.type !== node.type) ||
      textFields[node.type!].some(key => data[key] !== undefined && typeof data[key] !== "string") ||
      (support.fontSize && data.fontSize !== undefined && !finite.positive().safeParse(data.fontSize).success) ||
      (node.type === "stickyNote" && data.color !== undefined && (typeof data.color !== "string" || !isStickyColor(data.color))))
      return { ok: false, reason: "invalid" };
  }
  for (const edge of edges) {
    if (edge.type !== undefined && !edgeTypes.has(edge.type)) return { ok: false, reason: "unsupported" };
    if (edgeIds.has(edge.id) || !nodeIds.has(edge.source) || !nodeIds.has(edge.target) ||
      ((edge.type === undefined || edge.type === "custom") && !customDataSchema.safeParse(edge.data ?? {}).success))
      return { ok: false, reason: "invalid" };
    edgeIds.add(edge.id);
  }
  let normalized: CanvasGraph;
  try { normalized = normalizeGroups({ nodes: nodes as Node[], edges: edges as Edge[] }); }
  catch { return { ok: false, reason: "invalid" }; }
  const clean = persistableSnapshot({ format: "reactflow", version: 1,
    nodes: normalized.nodes.map(node => ({ ...node, data: node.data ?? {} })), edges: normalized.edges });
  return { ok: true, value: { format: "trillion-board-clipboard", version: 1, nodes: clean.nodes, edges: clean.edges } };
}

export function readCanvasClipboard(text: string): ClipboardResult {
  try { return validate(JSON.parse(text)); } catch { return { ok: false, reason: "invalid" }; }
}

export function captureCanvasSelection(graph: CanvasGraph): ClipboardResult {
  const selected = expandGroupSelection(graph.nodes, graph.nodes.filter(node => node.selected).map(node => node.id));
  const nodes = graph.nodes.filter(node => selected.has(node.id));
  if (!nodes.length) return { ok: false, reason: "empty" };
  const ids = new Set(nodes.map(node => node.id));
  try {
    return validate({ format: "trillion-board-clipboard", version: 1, nodes,
      edges: graph.edges.filter(edge => ids.has(edge.source) && ids.has(edge.target)) });
  } catch { return { ok: false, reason: "invalid" }; }
}

export function clipboardContentError(reason: "invalid" | "unsupported" | "empty"): string {
  return reason === "unsupported" ? "This selection contains unsupported Board content or parent containers."
    : reason === "empty" ? "Select one or more nodes first."
    : "Clipboard content is not a valid Board selection. Copy supported nodes in Board and try again.";
}
